import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Check, ArrowUpRight } from 'lucide-react';
import { PCCase, UserSubmittedCase, FormFactor } from '../../types';
import { Badge, Button } from '../ui';
import { useAppStore } from '../../context/store';
import {
  calculateVolumeLiters,
  formatDimensionValue,
  formatPrice,
  getAmazonSearchUrl,
} from '../../utils/conversions';
import { getLargestBoard } from '../../utils/formFactors';
import { OrthoDrawing } from './CaseDrawing';

// Shared scale for every card drawing, so sizes compare at a glance while scrolling
const DRAWING_SCALE = 0.26;

const boardLabels: Partial<Record<FormFactor, string>> = {
  'Micro-ATX': 'mATX',
};
interface CaseCardProps {
  pcCase: PCCase;
}

export const CaseCard: React.FC<CaseCardProps> = ({ pcCase }) => {
  const { addToComparison, removeFromComparison, isInComparison, comparison } = useAppStore();
  const [imageFailed, setImageFailed] = useState(false);

  const inComparison = isInComparison(pcCase.id);
  const comparisonFull = !inComparison && comparison.selectedCases.length >= 4;
  const unit = comparison.unitSystem === 'metric' ? 'mm' : 'in';
  const dims = pcCase.dimensions;
  const image = pcCase.thumbnailUrl || pcCase.imageUrl;
  const showImage = Boolean(image) && !imageFailed;
  const isUserCase = 'isUserSubmitted' in pcCase && (pcCase as UserSubmittedCase).isUserSubmitted;
  const board = getLargestBoard(pcCase.formFactorSupport);

  const pricedLinks = (pcCase.referralLinks ?? []).filter((link) => link.price > 0);
  const cheapest = pricedLinks.sort((a, b) => a.price - b.price)[0];
  const shopLink = cheapest ?? pcCase.referralLinks?.[0];

  const handleCompareClick = () => {
    if (inComparison) {
      removeFromComparison(pcCase.id);
    } else {
      addToComparison(pcCase.id);
    }
  };

  return (
    <article
      className={`
        group relative flex flex-col bg-surface border rounded-xl overflow-hidden
        transition-[border-color,transform] duration-150 hover:-translate-y-0.5
        ${inComparison ? 'border-accent ring-1 ring-accent' : 'border-line hover:border-line-strong'}
      `}
    >
      {/* Visual: product photo, or a to-scale drawing when there is none */}
      <Link
        to={`/case/${pcCase.id}`}
        className="relative block aspect-[4/3] bg-surface-2 bg-blueprint-sm border-b border-line"
      >
        {showImage ? (
          <img
            src={image}
            alt={`${pcCase.manufacturer} ${pcCase.name}`}
            loading="lazy"
            className="absolute inset-0 w-full h-full object-contain p-5 mix-blend-multiply dark:mix-blend-normal"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <>
            <span className="absolute top-3 left-3.5 font-mono text-[10px] uppercase tracking-label text-muted">
              Front · Side · {unit}
            </span>
            {dims && (
              <div className="absolute inset-x-0 bottom-4 flex justify-center">
                <OrthoDrawing dimensions={dims} scale={DRAWING_SCALE} unit={unit} selected={inComparison} />
              </div>
            )}
          </>
        )}

        {dims && (
          <span className="absolute top-2.5 right-2.5 font-mono text-[13px] font-medium px-2 py-0.5 rounded-md bg-surface border border-line">
            {calculateVolumeLiters(dims)} L
          </span>
        )}
        {isUserCase && showImage && (
          <Badge size="sm" className="absolute top-3 left-3 bg-surface">Community</Badge>
        )}
      </Link>

      {/* Info */}
      <div className="flex flex-1 flex-col gap-3.5 p-4">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <span className="font-mono text-2xs uppercase tracking-label text-muted truncate">
              {pcCase.manufacturer}
            </span>
            <Badge size="sm" className="shrink-0">{pcCase.sizeCategory}</Badge>
          </div>
          <Link
            to={`/case/${pcCase.id}`}
            className="text-lg font-semibold tracking-tight truncate hover:text-accent-ink transition-colors"
          >
            {pcCase.name}
          </Link>
          {dims && (
            <span className="font-mono text-xs text-muted">
              {formatDimensionValue(dims.height, unit)} × {formatDimensionValue(dims.width, unit)} × {formatDimensionValue(dims.depth, unit)} {unit}
            </span>
          )}
        </div>

        <dl className="grid grid-cols-3 gap-2 border-t border-line pt-3">
          <Spec label="GPU max" value={formatDimensionValue(pcCase.maxGpuLength, unit)} unit={unit} />
          <Spec label="Cooler max" value={formatDimensionValue(pcCase.maxCpuCoolerHeight, unit)} unit={unit} />
          <Spec label="Board" value={board ? (boardLabels[board] ?? board) : '—'} />
        </dl>

        <div className="mt-auto flex items-center justify-between gap-3">
          <a
            href={shopLink?.url ?? getAmazonSearchUrl(pcCase.manufacturer, pcCase.name)}
            target="_blank"
            rel="noopener noreferrer sponsored"
            className="inline-flex items-center gap-1 min-h-11 text-sm text-muted hover:text-fg transition-colors min-w-0"
          >
            {cheapest && (
              <span className="font-mono text-fg">{formatPrice(cheapest.price, cheapest.currency)}</span>
            )}
            {cheapest && <span aria-hidden="true">·</span>}
            <span className="truncate">{shopLink?.retailer ?? 'Amazon'}</span>
            <ArrowUpRight size={14} className="shrink-0" />
          </a>
          <Button
            variant={inComparison ? 'accent' : 'outline'}
            size="lg"
            onClick={handleCompareClick}
            disabled={comparisonFull}
            aria-pressed={inComparison}
            title={comparisonFull ? 'Comparison is full (4 cases)' : undefined}
            className="shrink-0"
          >
            {inComparison ? <Check size={16} /> : <Plus size={16} />}
            {inComparison ? 'In comparison' : 'Compare'}
          </Button>
        </div>
      </div>
    </article>
  );
};

interface SpecProps {
  label: string;
  value: string;
  unit?: string;
}

const Spec: React.FC<SpecProps> = ({ label, value, unit }) => (
  <div className="flex flex-col gap-0.5 min-w-0">
    <dt className="text-2xs text-muted">{label}</dt>
    <dd className="font-mono text-[15px] truncate">
      {value}
      {unit && <span className="text-2xs text-muted"> {unit}</span>}
    </dd>
  </div>
);
